import { useSelector, useDispatch } from "react-redux";
import axios from "axios";
import { RootState } from "../../../store";
import { IAppointment } from "../../../types/models/Appointment";
import { FETCH_APPOINTMENT_DATA } from "../../../store/calendar/constants";
import { config } from "../../../Constants";
import {
    toggleActivityByModal, 
    updateState,
    addNewAppointmentInfo,
    updateAppointment 
} from "../../../store/calendar/calendarReducer";
import useWindowWidth from "../../../hooks/useWindowWidth";
import { formatDateTime } from "../../../helpers/date-time/dateTimeHelpers";
import { getFormattedDateTime } from "../../../helpers/date-time/getFormattedDateTime";
import { rescheduleAppointment } from "../../../helpers/calendar/rescheduleAppointment";
import { handleApiError } from "../../../helpers/error-handlers/handleApiError";
import { getAuthTokenConfig } from "../../../helpers/others/getAuthTokenConfig";
import styles from "./AppointmentModal.module.css";
import { Modal, ModalBackground, ModalCloseButton, ModalContent } from "../../../components/common/Modal";
import InputField from "../common/InputField";
import CheckboxInputField from "../common/CheckboxInputField";
import EmailCheckbox from "../common/EmailCheckbox";
import SelectDate from "./SelectDate";

const ActivityByModal = () => {

    const dispatch = useDispatch();
    const calendarState = useSelector((state: RootState) => state.calendarState);
    const userState = useSelector((state: RootState) => state.userState);
    const { devicesWidth: { isLargeDevice } } = useWindowWidth();
    const { user } = userState;
    const appointment = calendarState.appointmentInAction as IAppointment
    const { 
        locationList,
        treatmentList,
        stylistList,
        calendarViewType,
        dayViewDate,
        weekViewDates,
        monthViewDates,
        activityType, 
        activityByLabel, 
        activityBy,
        paymentMethod, 
        paymentAmount,
        selectedDateForCustomRescheduling,
        startTimeForCustomRescheduling,
        couponCode, 
        giftCardCode,
        doNotSendEmailToClient,
        refundAppointmentPrice,
        refundRate,
        cancelReason,
        validationError
    } = calendarState;

    const paymentMadeOnline = appointment.paymentList?.find(payment => payment.online);

    const closeModal = () =>  {
        dispatch(updateState({ name: "activityBy", value: "" }));
        dispatch(toggleActivityByModal({
            activityType: "", 
            activityByLabel: "" 
        }));
        dispatch(addNewAppointmentInfo({
            name: "selectedDateForCustomRescheduling",
            value: null
        }));
        dispatch(addNewAppointmentInfo({
            name: "startTimeForCustomRescheduling",
            value: ""
        }));
        dispatch(updateState({ name: "validationError", value: false }))
    };

    const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = event.target;
        dispatch(updateState({ name, value }));
    }

    const refetchAppointmentData = () => {

        if(!user) return;

        let dates: Date[] = [];

        if(calendarViewType === "day") dates = [dayViewDate];
        if(calendarViewType === "week") dates = [...weekViewDates];
        if(calendarViewType === "month") dates = [...monthViewDates];

        const endDatePlusOne = new Date(dates[dates.length - 1].getTime() + 86400000);

        const { dateString: startDateString } = formatDateTime(dates[0]);
        const { dateString: endDateString } = formatDateTime(endDatePlusOne);

        dispatch({ 
            type: FETCH_APPOINTMENT_DATA, 
            payload: {
                tenantId: user.tenantId,
                startDateString,
                endDateString
            }
        });

    }

    const callApi = async(uri: string, requestBody: any) => {

        const endpoint = config.url.BACKEND_API_URL + uri;
        const authConfig = getAuthTokenConfig()

        dispatch(toggleActivityByModal({
            activityType: "", 
            activityByLabel: "" 
        }));
        dispatch(updateState({
            name: "appointmentInAction",
            payload: null
        }));

        try {

            const response = await axios.patch(endpoint, requestBody, authConfig);
            const message: string = response.data.message;

            dispatch(updateAppointment(message));

            refetchAppointmentData();

        } catch(error) {
            const { message } = handleApiError(error);
            dispatch(updateAppointment(message));
        }

    }
  
    const handleClick = async (newActivityType: string) => {

        if(validationError) dispatch(updateState({ name: "validationError", value: false }))
        
        if(!activityBy) {
            dispatch(updateState({ name: "validationError", value: true }))
            return
        }

        let uri = `/appointments/${appointment._id}/cancel`;
        const requestBody = {
            activityBy,
            couponCode,
            giftCardCode,
            paymentMethod, 
            paymentAmount: Number(paymentAmount),
            cancelReason,
            doNotSendEmailToClient,
            refundAppointmentPrice,
            refundRate: refundRate !== "" ? Number(refundRate) : 100  
        }

        if(newActivityType === "Reschedule") {

            if(selectedDateForCustomRescheduling) {

                if(!startTimeForCustomRescheduling) return alert("Please select a star time");

                dispatch(toggleActivityByModal({
                    activityType: "", 
                    activityByLabel: "" 
                }));
                dispatch(updateState({
                    name: "appointmentInAction",
                    payload: null
                }));

                const formattedDate = getFormattedDateTime(selectedDateForCustomRescheduling);
                const { day, monthInNumber, year } = formattedDate;
                const dateTimeString = `${year}-${monthInNumber}-${day}T${startTimeForCustomRescheduling}`

                const args = {
                    appointment_id: appointment._id ?? "",
                    doNotSendEmailToClient,
                    activityBy,
                    selectedStylist: appointment?.stylist.fullName ?? "",
                    selectedStylistId: appointment?.stylist._id ?? "",
                    selectedDateTime: dateTimeString
                }

                const message = await rescheduleAppointment(args);

                dispatch(updateAppointment(message));

                refetchAppointmentData();

                return;

            }

            const foundLocation = locationList.find(item => item._id === appointment.location?._id)
            const foundTreatment = treatmentList.find(item => item._id === appointment.treatment?._id);
            const foundStylist = stylistList.find(item => item._id === appointment.stylist._id);

            if(!foundTreatment || !foundStylist || !foundLocation) return;

            dispatch(addNewAppointmentInfo({ 
                name: "selectedTreatment",
                value: foundTreatment
            }));
            dispatch(addNewAppointmentInfo({ 
                name: "selectedStylist",
                value: foundStylist
            }))
            dispatch(addNewAppointmentInfo({ 
                name: "selectedLocation",
                value: foundLocation
            }))
            dispatch(updateState({ 
                name: "isRescheduling",
                value: true
            }));
            return;
            
        };

        if(newActivityType === "No Show") uri = `/appointments/${appointment._id}/make-no-show`;
        if(newActivityType === "Pay") uri = `/appointments/${appointment._id}/add-payment`;
        if(newActivityType === "Redeem Coupon") uri = `/appointments/${appointment._id}/apply-coupon-discount`;
        if(newActivityType === "Redeem Gift Card") uri = `/appointments/${appointment._id}/apply-gift-card-discount`;

        callApi(uri, requestBody);

    }

    const toggleRefundAppointmentPrice = () => {
        dispatch(updateState({
            name: "refundAppointmentPrice",
            value: !refundAppointmentPrice
        }))
    }

    return (
        <Modal>
            <ModalBackground />
            <ModalContent
                width = {isLargeDevice ? "30%" : "95%"}
            >
                <ModalCloseButton 
                    onClick={closeModal}
                    color={"orange"}
                    topPosition="10px"
                    rightPosition="20px"
                    fontSize="1.5rem"
                >
                    X
                </ModalCloseButton>
                <InputField 
                    labelText={activityByLabel}
                    type="text"
                    name="activityBy"
                    value={activityBy}
                    handleChange={handleInputChange}
                    validationError={validationError}
                    validationErrorMessage = "activity by can't be blank"
                />
                { 
                    activityType === "Reschedule"
                    &&
                    <>
                        <SelectDate />
                        <InputField 
                            labelText="Custom Start Time"
                            type="text"
                            name="startTimeForCustomRescheduling"
                            value={startTimeForCustomRescheduling}
                            handleChange={handleInputChange}
                        />
                    </>
                }
                { 
                    activityType === "Cancel"
                    &&
                    <>
                        <InputField 
                            labelText="Cancel Reason"
                            type="text"
                            name="cancelReason"
                            value={cancelReason}
                            handleChange={handleInputChange}
                            validationError={validationError}
                            validationErrorMessage = "cancel reason can't be blank"
                        />
                        {
                            paymentMadeOnline
                            ?
                            <CheckboxInputField 
                                name="refundAppointmentPrice"
                                value="Refund appointment price"
                                checked={refundAppointmentPrice}
                                handleChange={toggleRefundAppointmentPrice}
                            />
                            :
                            null
                        }
                        {
                            (paymentMadeOnline?.amount === appointment.priceAfterDiscount && refundAppointmentPrice)
                            ?
                            <InputField 
                                type="text"
                                name="refundRate"
                                placeholder="Refund Rate (Leave it blank for full refund)"
                                value={refundRate}
                                handleChange={handleInputChange}
                            />
                            :
                            null
                        }
                    </>
                }
                {
                    (activityType === "Cancel" || activityType === "Reschedule")
                    &&
                    <EmailCheckbox />
                }
                <div className = {styles.activity_confirm_button}>
                    <button onClick = {() => handleClick(activityType)}>
                        {activityType}
                    </button>
                </div>
            </ModalContent>
        </Modal>
    );

}

export default ActivityByModal;